if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type=search]');
      }

      // --header-height is the header's own height, but at the top of the
      // page the header sits below the announcement bar, so a bar pinned at
      // that offset overlapped the header. Measure the header's real bottom
      // edge each time the drawer opens instead.
      show(...args) {
        const header = document.querySelector('header.header');
        if (header) {
          const bottom = Math.max(0, header.getBoundingClientRect().bottom);
          this.style.setProperty('--search-drawer-top', `${bottom.toFixed(1)}px`);
        }
        return super.show(...args);
      }

      // The base class resolves the hide promise on the overlay's
      // transitionend. A closing drawer goes to visibility:hidden, which never
      // fires that event, so `hidden` was never re-applied and an invisible
      // full-screen layer stayed behind the bar. Resolve on a short timer
      // instead (or the transitionend if it ever arrives first), so Escape
      // closes cleanly every time.
      // Same problem on open: the overlay's transitionend doesn't reliably
      // fire for the top bar, so afterShow (which moves focus into the
      // input) never ran and keyboard users typed into nothing.
      showTransition() {
        const transition = super.showTransition();

        return new Promise((resolve) => {
          const fallback = setTimeout(resolve, 260);
          transition.then(() => {
            clearTimeout(fallback);
            resolve();
          });
        });
      }

      // The content fades in after a .35s delay and a visibility:hidden input
      // can't take focus, so keep retrying until it becomes visible.
      afterShow() {
        super.afterShow();

        const started = performance.now();
        const focusInput = () => {
          const input = this.input;
          if (!this.open || !input || document.activeElement === input) return;
          if (getComputedStyle(input).visibility === 'visible') {
            input.focus({ preventScroll: true });
            return;
          }
          if (performance.now() - started < 1000) requestAnimationFrame(focusInput);
        };
        focusInput();
      }

      hideTransition() {
        this.removeAttribute('active');

        return new Promise((resolve) => {
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            clearTimeout(fallback);
            resolve();
          };

          const fallback = setTimeout(finish, 180);
          this.overlay?.addEventListener('transitionend', finish, { once: true });
        });
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 150));
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.addEventListener('search:results', this.syncScrollable.bind(this));
      }

      /* The results list is capped in height, so when it overflows the last
         row is cut mid-way. A bottom fade signals "scroll for more", but
         only when there is actually more to scroll to — fading a list that
         already fits would dim its final row for no reason. */
      syncScrollable() {
        const scroll = this.querySelector('.search__results__scroll');

        if (!scroll) return;

        // rAF so layout reflects the freshly injected markup first.
        requestAnimationFrame(() => {
          const overflowing = scroll.scrollHeight - scroll.clientHeight > 1;

          scroll.toggleAttribute('scrollable', overflowing);
          scroll.scrollTop = 0;
        });
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        // Only the `.typed` element is the typewriter. The previous sibling is
        // the leading search icon whenever the typewriter is disabled, and
        // hiding "the typewriter" would hide the icon instead.
        const previous = this.input.previousElementSibling;

        return previous?.classList.contains('typed') ? previous : null;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        // Warm the SKU index while the shopper is still typing, so SKU
        // matches arrive with the first results rather than after them.
        this.constructor.loadSkuIndex();

        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(theme.routes.predictive_search_url, window.location.origin);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;

        this.dispatchEvent(new CustomEvent('search:results'));
        this.addSkuMatches(document.getElementById(id), this.getQuery());
      }

      // Shopify's predictive search only prefix-matches SKUs from three
      // characters, so "hg" found nothing while "hg1" did. Keep a small
      // SKU index of every product (from the compact ?view=sku-index
      // template, cached for the session) and add any SKU matches the
      // server results left out. /products.json is the fallback.
      static skuIndexPromise = null;

      static loadSkuIndex() {
        if (this.skuIndexPromise) return this.skuIndexPromise;

        const cacheKey = 'fe-sku-index-v2';
        try {
          const cached = JSON.parse(sessionStorage.getItem(cacheKey));
          if (Array.isArray(cached)) return (this.skuIndexPromise = Promise.resolve(cached));
        } catch (error) { /* storage unavailable: fall through to fetch */ }

        const root = theme.routes.root_url.replace(/\/$/, '');
        const fetchIndexPage = (page, items = []) =>
          fetch(`${root}/collections/all?view=sku-index&page=${page}`)
            .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`sku-index ${response.status}`))))
            .then((data) => {
              // { next, products } now; a bare array while a cached older render is still served.
              const products = Array.isArray(data) ? data : data?.products;
              if (!Array.isArray(products)) throw new Error('sku-index not a list');
              items.push(...products.map((item) => ({ ...item, url: `${root}${item.url}` })));
              const hasNext = Array.isArray(data) ? products.length === 250 : data.next === true;
              return hasNext ? fetchIndexPage(page + 1, items) : items;
            });
        const fetchPage = (page, items = []) =>
          fetch(`${root}/products.json?limit=250&page=${page}`)
            .then((response) => (response.ok ? response.json() : { products: [] }))
            .then(({ products = [] }) => {
              products.forEach((product) => {
                const variant = product.variants?.[0] || {};
                const skus = (product.variants || []).map((v) => (v.sku || '').toLowerCase()).filter(Boolean);
                if (!skus.length) return;
                items.push({
                  title: product.title,
                  url: `${root}/products/${product.handle}`,
                  skus,
                  sku: variant.sku || '',
                  image: product.images?.[0]?.src || '',
                  price: Math.round(parseFloat(variant.price || 0) * 100)
                });
              });
              return products.length === 250 ? fetchPage(page + 1, items) : items;
            });

        this.skuIndexPromise = fetchIndexPage(1).catch(() => fetchPage(1)).then((items) => {
          try { sessionStorage.setItem(cacheKey, JSON.stringify(items)); } catch (error) { /* ignore */ }
          return items;
        }).catch(() => []);

        return this.skuIndexPromise;
      }

      async addSkuMatches(container, query) {
        const term = query.toLowerCase().replace(/\s+/g, '');
        if (!container || term.length < 2 || !/^[a-z0-9-]+$/.test(term)) return;

        const index = await this.constructor.loadSkuIndex();
        if (this.getQuery().toLowerCase().replace(/\s+/g, '') !== term) return;

        const shown = new Set([...container.querySelectorAll('a[href*="/products/"]')]
          .map((link) => new URL(link.href, window.location.origin).pathname.replace(/\/$/, '')));
        const matches = index
          .filter((item) => item.skus.some((sku) => sku.startsWith(term) || (term.length >= 3 && sku.includes(term))))
          .filter((item) => !shown.has(new URL(item.url, window.location.origin).pathname))
          .slice(0, 8);
        if (!matches.length) return;

        const group = document.createElement('div');
        group.className = 'sku-matches grid gap-3 md:gap-4 w-full';
        group.innerHTML = `<p class="search__heading block text-xs uppercase leading-tight tracking-widest">Matching SKUs</p><ul class="sku-matches__list grid" role="list"></ul>`;
        const list = group.querySelector('ul');
        matches.forEach((item) => {
          const li = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'sku-match';
          link.href = item.url;
          link.innerHTML = `<span class="sku-match__media"></span><span class="sku-match__body"><span class="sku-match__title"></span><span class="sku-match__meta"><span class="sku-match__sku"></span><span class="sku-match__price"></span></span></span>`;
          if (item.image) {
            const img = document.createElement('img');
            img.src = /[?&]width=/.test(item.image) ? item.image : item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2');
            img.alt = '';
            img.loading = 'lazy';
            img.width = 80;
            img.height = 80;
            link.querySelector('.sku-match__media').appendChild(img);
          }
          link.querySelector('.sku-match__title').textContent = item.title;
          link.querySelector('.sku-match__sku').textContent = item.sku;
          link.querySelector('.sku-match__price').textContent = `${theme.Currency.formatMoney(item.price, theme.settings.moneyFormat)} ex GST`;
          li.appendChild(link);
          list.appendChild(li);
        });

        const scroll = container.querySelector('.search__results__scroll');
        const empty = container.querySelector('.search__empty');
        if (scroll) {
          scroll.prepend(group);
          if (empty) empty.remove();
        } else {
          container.prepend(group);
        }
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
